import React, { useRef } from "react";
import Popup from "devextreme-react/popup";
import Form, {
  SimpleItem,
  Label,
  RequiredRule,
  Item,
} from "devextreme-react/form";
import type { default as FormInstance } from "devextreme/ui/form";
import notify from "devextreme/ui/notify";
import axios, { AxiosError } from "axios";
import * as S from "./ComponentesGarantia.styles";
import { ApiError } from "../../../services/apiError";
import {
  ComponenteGarantia,
  CreaComponenteGarantiaDto,
} from "../../../dtos/ComponenteGarantia";
import { componentesGarantiaService } from "../../../services/componentesGarantiaService";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    return {
      message:
        error.response?.data?.message || "Ocurrió un error en el servidor.",
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return {
    message: "Ocurrió un error inesperado. Revisa tu conexión de red.",
  };
};

type ComponenteGarantiaFormPopupProps = {
  visible: boolean;
  editingItem: Partial<ComponenteGarantia> | null;
  onHiding: () => void;
  onSaved: (item: ComponenteGarantia) => void;
};

const ComponenteGarantiaFormPopup: React.FC<ComponenteGarantiaFormPopupProps> = ({
  visible,
  editingItem,
  onHiding,
  onSaved,
}) => {
  const formRef = useRef<FormInstance | null>(null);

  const handleSave = async () => {
    const form = formRef.current;
    if (!form) return;

    const validation = await form.validate();
    if (!validation.isValid) {
      notify("Complete todos los campos requeridos.", "warning", 2000);
      return;
    }

    const formData = form.option("formData") as ComponenteGarantia;
    const payload: CreaComponenteGarantiaDto = {
      codigo: formData.codigo?.trim() ?? "",
      nombre: formData.nombre?.trim() || null,
      activo: formData.activo ?? true,
    };

    try {
      let saved: ComponenteGarantia;
      if (formData.id) {
        saved = await componentesGarantiaService.update(formData.id, payload);
        notify("Componente actualizado con éxito.", "success", 2000);
      } else {
        saved = await componentesGarantiaService.create(payload);
        notify("Componente creado con éxito.", "success", 2000);
      }
      onSaved(saved);
      onHiding();
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    }
  };

  return (
    <Popup
      visible={visible}
      onHiding={onHiding}
      dragEnabled={false}
      showCloseButton
      showTitle
      title={
        editingItem?.id
          ? `Editar componente: ${editingItem.codigo}`
          : "Nuevo componente de garantía"
      }
      width={520}
      height="auto"
      className="custom-form-popup"
    >
      <S.FormContainer>
        <Form
          formData={editingItem}
          onInitialized={(e) => {
            formRef.current = e.component ?? null;
          }}
        >
          <SimpleItem dataField="codigo" editorOptions={{ maxLength: 20 }}>
            <Label text="Código" />
            <RequiredRule message="El código es requerido." />
          </SimpleItem>
          <SimpleItem dataField="nombre">
            <Label text="Nombre" />
          </SimpleItem>
          <Item
            dataField="activo"
            editorType="dxCheckBox"
            editorOptions={{ text: "Activo" }}
          />
        </Form>
      </S.FormContainer>
      <S.PopupFooter>
        <S.CancelButton onClick={onHiding}>Cancelar</S.CancelButton>
        <S.SaveButton onClick={handleSave}>Guardar</S.SaveButton>
      </S.PopupFooter>
    </Popup>
  );
};

export default ComponenteGarantiaFormPopup;
